import React, { useEffect, useMemo, useState } from "react";
import { db, storage } from "../firebaseConfig";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
// Legacy stylesheet stays imported (it still carries global rules); new
// styling is scoped under .opsui- (see OpsUi.css).
import "./Supplies.css";
import {
  OpsModal,
  ConfirmDialog,
  Pill,
  StatusPill,
  Field,
  EmptyState,
  SortHeader,
  daysUntil,
  formatDate,
} from "./OpsUi";

// Pest-control categories first; the original list is kept so existing
// records still match a category.
const CATEGORIES = [
  "Pesticides & Chemicals",
  "Baits & Bait Stations",
  "Traps & Monitors",
  "Application Equipment",
  "PPE & Safety Equipment",
  "Exclusion Materials",
  "Hand Tools", "Power Tools", "Fasteners & Hardware", "Electrical Supplies", "Plumbing Supplies",
  "Concrete & Masonry Supplies", "Lumber & Wood Supplies", "Paint & Finishing Supplies", "Safety Equipment",
  "Ladders & Scaffolding", "Adhesives & Sealants", "Roofing & Insulation", "Drywall & Framing",
  "Metal & Structural Materials", "Site Equipment", "Temporary Structures", "HVAC & Ventilation",
  "Demolition & Cleanup Supplies", "Measuring & Layout Tools", "Heavy Equipment & Machinery",
  "Miscellaneous Supplies",
];

const UNITS = [
  "Pieces", "Boxes", "Rolls", "Gallons", "Cartons", "Length", "Sq. Ft.", "Bags",
  "Bottles", "Jugs", "Ounces", "Pounds", "Cases", "Stations",
];

const WAREHOUSE_STATUSES = ["Available", "Out of Stock", "Low Stock", "Reserved"];
const ORDER_STATUSES = ["Pending", "Ordered", "Back-Order", "On Hold", "Delivered", "Arrived", "Active"];
const ITEMS_PER_PAGE = 25;

const EMPTY_ORDER = {
  siteName: "",
  material: "",
  store: "",
  sku: "",
  dateNeeded: "",
  arrivalDate: "",
  techAssign: "",
  notes: "",
  images: [],
  cost: "",
  status: "Pending",
};

const EMPTY_WAREHOUSE = {
  itemName: "",
  itemDescription: "",
  category: "",
  itemCode: "",
  quantity: "",
  units: "",
  aisleNumber: "",
  shelfNumber: "",
  binNumber: "",
  restockLevel: "",
  checkedDate: "",
  checkedBy: "",
  supplier: "",
  status: "Available",
  images: [],
};

const EMPTY_ORDER_ITEM = { material: "", store: "", sku: "", cost: "", images: [] };

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && String(v ?? "").trim() !== "" ? n : null;
};

const money = (v) => {
  const n = num(String(v ?? "").replace(/[$,]/g, ""));
  return n === null ? "—" : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
};

const isLowStock = (s) => {
  const q = num(s.quantity);
  const r = num(s.restockLevel);
  return q !== null && r !== null && q <= r;
};

const stockState = (s) => {
  const q = num(s.quantity);
  if (q !== null && q <= 0) return { tone: "danger", label: "Out of stock" };
  if (isLowStock(s)) return { tone: "warn", label: "Low stock" };
  return null;
};

const isOrderClosed = (s) => ["delivered", "arrived"].includes(String(s.status || "").toLowerCase());

const Supplies = () => {
  const [tab, setTab] = useState("orders"); // orders | warehouse
  const [supplies, setSupplies] = useState([]);
  const [warehouseSupplies, setWarehouseSupplies] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  // orders
  const [orderForm, setOrderForm] = useState(null); // { mode, data, files }
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("open");
  const [orderSort, setOrderSort] = useState({ key: "dateNeeded", dir: "asc" });
  const [itemForm, setItemForm] = useState(null); // new order line item

  // warehouse
  const [warehouseForm, setWarehouseForm] = useState(null); // { mode, data, files }
  const [selectedWarehouseSupply, setSelectedWarehouseSupply] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [whSort, setWhSort] = useState({ key: "category", dir: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [useItemData, setUseItemData] = useState(null); // { quantity, user, job }
  const [noteData, setNoteData] = useState(null); // { text, images }

  const [deleteTarget, setDeleteTarget] = useState(null); // { type, item }
  const [busy, setBusy] = useState(false);

  // --------------------------------------------------------------- load
  useEffect(() => {
    const load = async () => {
      try {
        const [suppliesSnap, whSnap, usersSnap, jobsSnap, propsSnap] = await Promise.all([
          getDocs(collection(db, "supplies")),
          getDocs(collection(db, "warehouseSupplies")),
          getDocs(collection(db, "users")),
          getDocs(collection(db, "jobs")),
          getDocs(collection(db, "properties")).catch(() => ({ docs: [] })),
        ]);
        setSupplies(suppliesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setWarehouseSupplies(whSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setUsers(
          usersSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(`${b.firstName || ""} ${b.lastName || ""}`))
        );
        setJobs(jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((job) => job.status === "Active"));
        setProperties(
          Array.from(
            new Set(
              propsSnap.docs
                .map((d) => d.data())
                .map((p) => p.propertyName || p.siteName || p.name)
                .filter(Boolean)
            )
          ).sort((a, b) => a.localeCompare(b))
        );
      } catch (e) {
        console.error("Error loading supplies:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const userName = (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim();

  const uploadImages = async (files, folder = "supplies") => {
    const uploads = Array.from(files || []).map(async (file) => {
      const imageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
      await uploadBytes(imageRef, file, { contentType: file.type });
      return getDownloadURL(imageRef);
    });
    return Promise.all(uploads);
  };

  // =============================================================== ORDERS
  const openNewOrder = () => setOrderForm({ mode: "add", data: { ...EMPTY_ORDER }, files: [] });
  const openEditOrder = () =>
    selectedSupply && setOrderForm({ mode: "edit", data: { ...EMPTY_ORDER, ...selectedSupply }, files: [] });

  const onOrderChange = (e) => {
    const { name, value } = e.target;
    setOrderForm((prev) => ({ ...prev, data: { ...prev.data, [name]: value } }));
  };

  const saveOrder = async () => {
    const { mode, data, files } = orderForm;
    const required = [
      ["siteName", "Property"],
      ["material", "Material"],
      ["dateNeeded", "Date Needed"],
    ];
    for (const [f, label] of required) {
      if (!String(data[f] || "").trim()) {
        alert(`Please fill out ${label}.`);
        return;
      }
    }
    setBusy(true);
    try {
      const uploaded = files.length ? await uploadImages(files, "supplies") : [];
      const payload = {
        ...data,
        images: [...(Array.isArray(data.images) ? data.images.filter((i) => typeof i === "string") : []), ...uploaded],
      };
      const writePayload = { ...payload };
      delete writePayload.id;
      if (mode === "add") {
        const docRef = await addDoc(collection(db, "supplies"), writePayload);
        setSupplies((prev) => [...prev, { id: docRef.id, ...writePayload }]);
      } else {
        await updateDoc(doc(db, "supplies", data.id), writePayload);
        setSupplies((prev) => prev.map((s) => (s.id === data.id ? payload : s)));
        setSelectedSupply(payload);
      }
      setOrderForm(null);
    } catch (e) {
      console.error("Error saving supply:", e);
      alert("Could not save supply request.");
    } finally {
      setBusy(false);
    }
  };

  const updateSupplyFields = async (supply, patch) => {
    await updateDoc(doc(db, "supplies", supply.id), patch);
    const next = { ...supply, ...patch };
    setSupplies((prev) => prev.map((s) => (s.id === supply.id ? next : s)));
    setSelectedSupply((prev) => (prev && prev.id === supply.id ? next : prev));
    return next;
  };

  const handleStatusUpdate = async (status) => {
    if (!selectedSupply) return;
    const patch = { status };
    if (["Delivered", "Arrived"].includes(status) && !selectedSupply.arrivalDate) {
      const now = new Date();
      patch.arrivalDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    }
    try {
      await updateSupplyFields(selectedSupply, patch);
    } catch (e) {
      console.error("Error updating status:", e);
    }
  };

  // line items ("Add Item to This Project") — persisted on the supply doc as noteItems
  const saveOrderItem = async () => {
    if (!selectedSupply || !itemForm) return;
    if (!String(itemForm.material || "").trim()) {
      alert("Please enter the material.");
      return;
    }
    setBusy(true);
    try {
      const images = itemForm.images.length ? await uploadImages(itemForm.images, "supplies") : [];
      const item = { ...itemForm, images, received: false, ordered: false };
      const noteItems = [...(Array.isArray(selectedSupply.noteItems) ? selectedSupply.noteItems : []), item];
      await updateSupplyFields(selectedSupply, { noteItems });
      setItemForm(null);
    } catch (e) {
      console.error("Error adding item:", e);
      alert("Could not add item.");
    } finally {
      setBusy(false);
    }
  };

  const toggleOrderItem = async (index, field) => {
    const items = [...(selectedSupply.noteItems || [])];
    if (!items[index]) return;
    items[index] = { ...items[index], [field]: !items[index][field] };
    try {
      await updateSupplyFields(selectedSupply, { noteItems: items });
    } catch (e) {
      console.error("Error updating item:", e);
    }
  };

  const deleteOrderItem = async (index) => {
    if (!window.confirm("Remove this item?")) return;
    const items = (selectedSupply.noteItems || []).filter((_, i) => i !== index);
    try {
      await updateSupplyFields(selectedSupply, { noteItems: items });
    } catch (e) {
      console.error("Error removing item:", e);
    }
  };

  const orderRows = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    return supplies
      .filter((s) => {
        if (orderStatusFilter === "open" && isOrderClosed(s)) return false;
        if (orderStatusFilter === "closed" && !isOrderClosed(s)) return false;
        if (!q) return true;
        return [s.siteName, s.material, s.store, s.sku, s.techAssign, s.status]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const dir = orderSort.dir === "asc" ? 1 : -1;
        const av = String(a[orderSort.key] || (orderSort.key === "dateNeeded" ? "9999" : ""));
        const bv = String(b[orderSort.key] || (orderSort.key === "dateNeeded" ? "9999" : ""));
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * dir;
      });
  }, [supplies, orderSearch, orderStatusFilter, orderSort]);

  const orderStats = useMemo(() => {
    const open = supplies.filter((s) => !isOrderClosed(s));
    const late = open.filter((s) => {
      const d = daysUntil(s.dateNeeded);
      return d !== null && d < 0;
    });
    const openCost = open.reduce((sum, s) => sum + (num(String(s.cost ?? "").replace(/[$,]/g, "")) || 0), 0);
    return { open: open.length, late: late.length, closed: supplies.length - open.length, openCost };
  }, [supplies]);

  // =============================================================== WAREHOUSE
  const openNewWarehouse = () => setWarehouseForm({ mode: "add", data: { ...EMPTY_WAREHOUSE }, files: [] });
  const openEditWarehouse = () =>
    selectedWarehouseSupply &&
    setWarehouseForm({ mode: "edit", data: { ...EMPTY_WAREHOUSE, ...selectedWarehouseSupply }, files: [] });

  const onWarehouseChange = (e) => {
    const { name, value } = e.target;
    setWarehouseForm((prev) => ({ ...prev, data: { ...prev.data, [name]: value } }));
  };

  const currentUserName = () => {
    const uid = getAuth().currentUser?.uid;
    const u = users.find((user) => user.id === uid);
    return u ? userName(u) : "Unknown User";
  };

  const saveWarehouse = async () => {
    const { mode, data, files } = warehouseForm;
    const required = [
      ["itemName", "Item Name"],
      ["category", "Category"],
      ["quantity", "Quantity"],
      ["units", "Units"],
    ];
    for (const [f, label] of required) {
      if (String(data[f] ?? "").trim() === "") {
        alert(`Please fill out ${label}.`);
        return;
      }
    }
    setBusy(true);
    try {
      const uploaded = files.length ? await uploadImages(files, "supplies") : [];
      const payload = {
        ...data,
        quantity: num(data.quantity) ?? data.quantity,
        restockLevel: num(data.restockLevel) ?? data.restockLevel,
        images: [...(Array.isArray(data.images) ? data.images.filter((i) => typeof i === "string") : []), ...uploaded],
      };
      if (mode === "add") {
        payload.checkedBy = currentUserName();
        payload.checkedDate = new Date().toLocaleString();
      }
      const writePayload = { ...payload };
      delete writePayload.id;
      if (mode === "add") {
        const docRef = await addDoc(collection(db, "warehouseSupplies"), writePayload);
        setWarehouseSupplies((prev) => [...prev, { id: docRef.id, ...writePayload }]);
      } else {
        await updateDoc(doc(db, "warehouseSupplies", data.id), writePayload);
        setWarehouseSupplies((prev) => prev.map((s) => (s.id === data.id ? payload : s)));
        setSelectedWarehouseSupply(payload);
      }
      setWarehouseForm(null);
    } catch (e) {
      console.error("Error saving warehouse supply:", e);
      alert("Could not save item.");
    } finally {
      setBusy(false);
    }
  };

  const replaceWarehouse = (next) => {
    setWarehouseSupplies((prev) => prev.map((s) => (s.id === next.id ? next : s)));
    setSelectedWarehouseSupply((prev) => (prev && prev.id === next.id ? next : prev));
  };

  const handleUseItemSubmit = async () => {
    const { quantity, user, job } = useItemData;
    const used = parseInt(quantity, 10);
    if (!used || used <= 0) {
      alert("Enter a quantity greater than zero.");
      return;
    }
    if (!user) {
      alert("Select who is using the item.");
      return;
    }
    const s = selectedWarehouseSupply;
    const newQuantity = (num(s.quantity) || 0) - used;
    if (newQuantity < 0) {
      alert("Insufficient quantity in stock.");
      return;
    }
    setBusy(true);
    try {
      const userDoc = users.find((u) => u.id === user);
      const jobDoc = jobs.find((j) => j.id === job);
      const note = {
        quantity,
        user: userDoc ? userName(userDoc) : "Unknown User",
        job,
        jobLabel: jobDoc ? [jobDoc.siteName, jobDoc.scope].filter(Boolean).join(" - ") : "",
        timestamp: new Date().toLocaleString(),
      };
      const notes = Array.isArray(s.notes) ? [...s.notes, note] : [note];
      await updateDoc(doc(db, "warehouseSupplies", s.id), { quantity: newQuantity, notes });
      replaceWarehouse({ ...s, quantity: newQuantity, notes });
      setUseItemData(null);
    } catch (e) {
      console.error("Error using item:", e);
      alert("Could not record usage.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddNoteSubmit = async () => {
    if (!String(noteData.text || "").trim()) {
      alert("Please enter a note.");
      return;
    }
    setBusy(true);
    try {
      const imageUrls = noteData.images.length ? await uploadImages(noteData.images, "warehouseNotes") : [];
      const s = selectedWarehouseSupply;
      const newNote = { text: noteData.text.trim(), images: imageUrls, timestamp: new Date().toLocaleString() };
      const notes = Array.isArray(s.notes) ? [...s.notes, newNote] : [newNote];
      await updateDoc(doc(db, "warehouseSupplies", s.id), { notes });
      replaceWarehouse({ ...s, notes });
      setNoteData(null);
    } catch (e) {
      console.error("Error adding note:", e);
      alert("Could not save note.");
    } finally {
      setBusy(false);
    }
  };

  const warehouseCategories = useMemo(() => {
    const used = new Set(warehouseSupplies.map((s) => s.category).filter(Boolean));
    return CATEGORIES.filter((c) => used.has(c)).concat([...used].filter((c) => !CATEGORIES.includes(c)));
  }, [warehouseSupplies]);

  const filteredWarehouseSupplies = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const nameKey = (s) => String(s.itemName || "").toLowerCase();
    return warehouseSupplies
      .filter((s) => {
        if (categoryFilter && s.category !== categoryFilter) return false;
        if (stockFilter === "low" && !stockState(s)) return false;
        if (!q) return true;
        return [s.itemName, s.itemCode, s.itemDescription, s.supplier]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const dir = whSort.dir === "asc" ? 1 : -1;
        if (whSort.key === "quantity") {
          return ((num(a.quantity) ?? -1) - (num(b.quantity) ?? -1)) * dir;
        }
        if (whSort.key === "category") {
          const c = String(a.category || "").localeCompare(String(b.category || "")) * dir;
          if (c !== 0) return c;
        }
        // names starting with a digit sort first, like the original page
        const an = nameKey(a);
        const bn = nameKey(b);
        const ad = /^[0-9]/.test(an);
        const bd = /^[0-9]/.test(bn);
        if (ad !== bd) return ad ? -1 : 1;
        return an.localeCompare(bn) * (whSort.key === "itemName" ? dir : 1);
      });
  }, [warehouseSupplies, searchTerm, categoryFilter, stockFilter, whSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, stockFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredWarehouseSupplies.length / ITEMS_PER_PAGE));
  const paginatedWarehouseSupplies = filteredWarehouseSupplies.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const whStats = useMemo(() => {
    let low = 0;
    let out = 0;
    warehouseSupplies.forEach((s) => {
      const st = stockState(s);
      if (st?.tone === "danger") out += 1;
      else if (st) low += 1;
    });
    return { total: warehouseSupplies.length, low, out, categories: new Set(warehouseSupplies.map((s) => s.category).filter(Boolean)).size };
  }, [warehouseSupplies]);

  // =============================================================== delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      if (deleteTarget.type === "supply") {
        await deleteDoc(doc(db, "supplies", deleteTarget.item.id));
        setSupplies((prev) => prev.filter((s) => s.id !== deleteTarget.item.id));
        setSelectedSupply(null);
      } else {
        await deleteDoc(doc(db, "warehouseSupplies", deleteTarget.item.id));
        setWarehouseSupplies((prev) => prev.filter((s) => s.id !== deleteTarget.item.id));
        setSelectedWarehouseSupply(null);
      }
      setDeleteTarget(null);
    } catch (e) {
      console.error("Error deleting:", e);
      alert("Could not delete.");
    } finally {
      setBusy(false);
    }
  };

  const sortToggle = (setter) => (key) =>
    setter((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const location = (s) =>
    [s.aisleNumber && `Aisle ${s.aisleNumber}`, s.shelfNumber && `Shelf ${s.shelfNumber}`, s.binNumber && `Bin ${s.binNumber}`]
      .filter(Boolean)
      .join(" · ");

  // =============================================================== render pieces
  const renderOrdersTab = () => (
    <>
      <div className="opsui-stats">
        <div className="opsui-stat">
          <div className="opsui-stat-label">Open requests</div>
          <div className="opsui-stat-value">{orderStats.open}</div>
        </div>
        <div className={`opsui-stat ${orderStats.late ? "is-danger" : ""}`}>
          <div className="opsui-stat-label">Past needed date</div>
          <div className="opsui-stat-value">{orderStats.late}</div>
        </div>
        <div className="opsui-stat">
          <div className="opsui-stat-label">Open cost</div>
          <div className="opsui-stat-value">{money(orderStats.openCost)}</div>
        </div>
        <div className="opsui-stat is-success">
          <div className="opsui-stat-label">Delivered / arrived</div>
          <div className="opsui-stat-value">{orderStats.closed}</div>
        </div>
      </div>

      <div className="opsui-card">
        <div className="opsui-toolbar">
          <input
            className="opsui-input"
            type="search"
            placeholder="Search property, material, store, tech…"
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
          />
          <select className="opsui-select" value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}>
            <option value="open">Open requests</option>
            <option value="closed">Delivered / arrived</option>
            <option value="all">All requests</option>
          </select>
          <span className="opsui-toolbar-spacer" />
          <button type="button" className="opsui-btn is-primary" onClick={openNewOrder}>
            + New supply request
          </button>
        </div>

        {loading ? (
          <EmptyState title="Loading…" />
        ) : orderRows.length === 0 ? (
          <EmptyState
            title={supplies.length ? "No requests match" : "No supply requests yet"}
            action={
              !supplies.length && (
                <button type="button" className="opsui-btn is-primary" onClick={openNewOrder}>
                  + New supply request
                </button>
              )
            }
          >
            {!supplies.length && "Request materials a tech needs for a property and track them until they arrive."}
          </EmptyState>
        ) : (
          <div className="opsui-table-wrap">
            <table className="opsui-table">
              <thead>
                <tr>
                  <SortHeader label="Property" sortKey="siteName" sort={orderSort} onSort={sortToggle(setOrderSort)} />
                  <SortHeader label="Material" sortKey="material" sort={orderSort} onSort={sortToggle(setOrderSort)} />
                  <SortHeader label="Needed" sortKey="dateNeeded" sort={orderSort} onSort={sortToggle(setOrderSort)} />
                  <th className="opsui-hide-sm">Arrival</th>
                  <SortHeader label="Tech" sortKey="techAssign" sort={orderSort} onSort={sortToggle(setOrderSort)} className="opsui-hide-sm" />
                  <th className="opsui-hide-sm" style={{ textAlign: "right" }}>Cost</th>
                  <SortHeader label="Status" sortKey="status" sort={orderSort} onSort={sortToggle(setOrderSort)} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {orderRows.map((s) => {
                  const d = daysUntil(s.dateNeeded);
                  const late = !isOrderClosed(s) && d !== null && d < 0;
                  return (
                    <tr key={s.id} className={`is-clickable${late ? " is-alert" : ""}`} onClick={() => setSelectedSupply(s)}>
                      <td className="opsui-cell-main">{s.siteName || "—"}</td>
                      <td>
                        <div>{s.material || "—"}</div>
                        <div className="opsui-cell-sub">{[s.store, s.sku && `#${s.sku}`].filter(Boolean).join(" · ")}</div>
                      </td>
                      <td>
                        {s.dateNeeded ? formatDate(s.dateNeeded) : "—"}
                        {late && <div><Pill tone="danger">Late</Pill></div>}
                        {!late && !isOrderClosed(s) && d !== null && d <= 3 && <div><Pill tone="warn">{d === 0 ? "Today" : `In ${d}d`}</Pill></div>}
                      </td>
                      <td className="opsui-hide-sm">{s.arrivalDate ? formatDate(s.arrivalDate) : <span className="opsui-muted">Pending</span>}</td>
                      <td className="opsui-hide-sm">{s.techAssign || "—"}</td>
                      <td className="opsui-hide-sm" style={{ textAlign: "right" }}>{money(s.cost)}</td>
                      <td><StatusPill status={s.status} /></td>
                      <td className="opsui-cell-actions" onClick={(e) => e.stopPropagation()}>
                        <button type="button" className="opsui-btn is-sm" onClick={() => setSelectedSupply(s)}>
                          Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );

  const renderWarehouseTab = () => (
    <>
      <div className="opsui-stats">
        <div className="opsui-stat">
          <div className="opsui-stat-label">Items tracked</div>
          <div className="opsui-stat-value">{whStats.total}</div>
        </div>
        <div className={`opsui-stat ${whStats.low ? "is-warn" : ""}`}>
          <div className="opsui-stat-label">Low stock</div>
          <div className="opsui-stat-value">{whStats.low}</div>
        </div>
        <div className={`opsui-stat ${whStats.out ? "is-danger" : ""}`}>
          <div className="opsui-stat-label">Out of stock</div>
          <div className="opsui-stat-value">{whStats.out}</div>
        </div>
        <div className="opsui-stat">
          <div className="opsui-stat-label">Categories</div>
          <div className="opsui-stat-value">{whStats.categories}</div>
        </div>
      </div>

      <div className="opsui-card">
        <div className="opsui-toolbar">
          <input
            className="opsui-input"
            type="search"
            placeholder="Search item name, code, supplier…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select className="opsui-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {warehouseCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select className="opsui-select" value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}>
            <option value="all">All stock levels</option>
            <option value="low">Low / out of stock</option>
          </select>
          <span className="opsui-toolbar-spacer" />
          <button type="button" className="opsui-btn is-primary" onClick={openNewWarehouse}>
            + Add warehouse item
          </button>
        </div>

        {loading ? (
          <EmptyState title="Loading…" />
        ) : filteredWarehouseSupplies.length === 0 ? (
          <EmptyState
            title={warehouseSupplies.length ? "No items match" : "No warehouse items yet"}
            action={
              !warehouseSupplies.length && (
                <button type="button" className="opsui-btn is-primary" onClick={openNewWarehouse}>
                  + Add warehouse item
                </button>
              )
            }
          >
            {!warehouseSupplies.length && "Track chemicals, baits, equipment and PPE on hand, with restock alerts."}
          </EmptyState>
        ) : (
          <>
            <div className="opsui-table-wrap">
              <table className="opsui-table">
                <thead>
                  <tr>
                    <SortHeader label="Item" sortKey="itemName" sort={whSort} onSort={sortToggle(setWhSort)} />
                    <SortHeader label="Category" sortKey="category" sort={whSort} onSort={sortToggle(setWhSort)} className="opsui-hide-sm" />
                    <SortHeader label="On hand" sortKey="quantity" sort={whSort} onSort={sortToggle(setWhSort)} />
                    <th className="opsui-hide-sm">Location</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {paginatedWarehouseSupplies.map((s) => {
                    const st = stockState(s);
                    return (
                      <tr
                        key={s.id}
                        className={`is-clickable${st ? " is-alert" : ""}`}
                        onClick={() => setSelectedWarehouseSupply(s)}
                      >
                        <td>
                          <div className="opsui-cell-main">{s.itemName || "Unnamed item"}</div>
                          <div className="opsui-cell-sub">
                            {[s.itemCode && `#${s.itemCode}`, s.itemDescription].filter(Boolean).join(" · ").slice(0, 90)}
                          </div>
                        </td>
                        <td className="opsui-hide-sm">{s.category || "—"}</td>
                        <td>
                          <strong>{s.quantity ?? "—"}</strong> <span className="opsui-muted">{s.units}</span>
                          {num(s.restockLevel) !== null && (
                            <div className="opsui-cell-sub">Restock at {s.restockLevel}</div>
                          )}
                        </td>
                        <td className="opsui-hide-sm">{location(s) || <span className="opsui-muted">—</span>}</td>
                        <td>
                          <div className="opsui-pills">
                            {st && <Pill tone={st.tone}>{st.label}</Pill>}
                            {s.status && (!st || s.status !== st.label) && <StatusPill status={s.status} />}
                          </div>
                        </td>
                        <td className="opsui-cell-actions" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="opsui-btn is-sm" onClick={() => setSelectedWarehouseSupply(s)}>
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="opsui-pager">
                <span className="opsui-count">
                  {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                  {Math.min(currentPage * ITEMS_PER_PAGE, filteredWarehouseSupplies.length)} of {filteredWarehouseSupplies.length}
                </span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="opsui-btn is-sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`opsui-btn is-sm${p === currentPage ? " is-primary" : ""}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="opsui-btn is-sm"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const techOptions = (
    <>
      <option value="">Select technician</option>
      {users.map((u) => (
        <option key={u.id} value={userName(u)}>
          {userName(u)}
        </option>
      ))}
    </>
  );

  // =============================================================== render
  return (
    <div className="opsui-root opsui-page">
      <div className="opsui-header">
        <div>
          <h1 className="opsui-title">Supplies</h1>
          <p className="opsui-subtitle">Supply requests for properties and warehouse inventory on hand.</p>
        </div>
      </div>

      <div className="opsui-tabs">
        <button type="button" className={`opsui-tab${tab === "orders" ? " is-active" : ""}`} onClick={() => setTab("orders")}>
          Supplies to order <span className="opsui-tab-count">{orderStats.open}</span>
        </button>
        <button
          type="button"
          className={`opsui-tab${tab === "warehouse" ? " is-active" : ""}`}
          onClick={() => setTab("warehouse")}
        >
          Warehouse inventory <span className="opsui-tab-count">{warehouseSupplies.length}</span>
        </button>
      </div>

      {tab === "orders" ? renderOrdersTab() : renderWarehouseTab()}

      {/* ================= supply request form ================= */}
      {orderForm && (
        <OpsModal
          title={orderForm.mode === "add" ? "New supply request" : "Edit supply request"}
          onClose={() => setOrderForm(null)}
          stacked={!!selectedSupply}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setOrderForm(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveOrder} disabled={busy}>
                {busy ? "Saving…" : "Save request"}
              </button>
            </>
          }
        >
          <div className="opsui-form-grid">
            <Field label="Property" required wide>
              <input className="opsui-input" name="siteName" list="opsui-supply-props" value={orderForm.data.siteName || ""} onChange={onOrderChange} />
              <datalist id="opsui-supply-props">
                {properties.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </Field>
            <Field label="Material" required>
              <input className="opsui-input" name="material" value={orderForm.data.material || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Store">
              <input className="opsui-input" name="store" value={orderForm.data.store || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Item #">
              <input className="opsui-input" name="sku" value={orderForm.data.sku || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Cost">
              <input className="opsui-input" type="number" step="0.01" name="cost" value={orderForm.data.cost || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Date Needed" required>
              <input className="opsui-input" type="date" name="dateNeeded" value={orderForm.data.dateNeeded || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Arrival Date">
              <input className="opsui-input" type="date" name="arrivalDate" value={orderForm.data.arrivalDate || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Tech">
              <select className="opsui-select" name="techAssign" value={orderForm.data.techAssign || ""} onChange={onOrderChange}>
                {techOptions}
                {orderForm.data.techAssign && !users.some((u) => userName(u) === orderForm.data.techAssign) && (
                  <option value={orderForm.data.techAssign}>{orderForm.data.techAssign}</option>
                )}
              </select>
            </Field>
            <Field label="Status">
              <select className="opsui-select" name="status" value={orderForm.data.status || ""} onChange={onOrderChange}>
                <option value="">Select status</option>
                {ORDER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                {orderForm.data.status && !ORDER_STATUSES.includes(orderForm.data.status) && (
                  <option value={orderForm.data.status}>{orderForm.data.status}</option>
                )}
              </select>
            </Field>
            <Field label="Notes" wide>
              <textarea className="opsui-textarea" name="notes" value={orderForm.data.notes || ""} onChange={onOrderChange} />
            </Field>
            <Field label="Images" wide hint={orderForm.files.length ? `${orderForm.files.length} image(s) will upload on save.` : undefined}>
              <input
                className="opsui-file"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setOrderForm((p) => ({ ...p, files }));
                }}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {/* ================= supply request details ================= */}
      {selectedSupply && (
        <OpsModal
          title={selectedSupply.material || "Supply request"}
          subtitle={selectedSupply.siteName}
          onClose={() => setSelectedSupply(null)}
          footer={
            <>
              <div className="opsui-foot-left">
                <button type="button" className="opsui-btn is-danger" onClick={() => setDeleteTarget({ type: "supply", item: selectedSupply })}>
                  Delete
                </button>
              </div>
              <button type="button" className="opsui-btn" onClick={openEditOrder}>
                Edit
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={() => setSelectedSupply(null)}>
                Close
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 18 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <StatusPill status={selectedSupply.status} />
              <span className="opsui-hint">Update status:</span>
              <select
                className="opsui-select"
                style={{ width: "auto", minWidth: 160 }}
                value={selectedSupply.status || ""}
                onChange={(e) => handleStatusUpdate(e.target.value)}
              >
                <option value="">—</option>
                {ORDER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <dl className="opsui-dl">
              <div><dt>Property</dt><dd>{selectedSupply.siteName || "—"}</dd></div>
              <div><dt>Tech</dt><dd>{selectedSupply.techAssign || "—"}</dd></div>
              <div><dt>Store</dt><dd>{selectedSupply.store || "—"}</dd></div>
              <div><dt>Item #</dt><dd>{selectedSupply.sku || "—"}</dd></div>
              <div><dt>Date Needed</dt><dd>{formatDate(selectedSupply.dateNeeded) || "—"}</dd></div>
              <div><dt>Arrival Date</dt><dd>{formatDate(selectedSupply.arrivalDate) || "Pending"}</dd></div>
              <div><dt>Cost</dt><dd>{money(selectedSupply.cost)}</dd></div>
              <div className="is-wide"><dt>Notes</dt><dd>{selectedSupply.notes || "—"}</dd></div>
            </dl>

            {Array.isArray(selectedSupply.images) && selectedSupply.images.length > 0 && (
              <div className="opsui-thumbs">
                {selectedSupply.images.map((img, i) => (
                  <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                    <img src={img} alt={`Supply ${i + 1}`} />
                  </a>
                ))}
              </div>
            )}

            <div className="opsui-card" style={{ margin: 0 }}>
              <div className="opsui-card-head">
                <h3 className="opsui-card-title">Items for this project</h3>
                <button type="button" className="opsui-btn is-sm" onClick={() => setItemForm({ ...EMPTY_ORDER_ITEM })}>
                  + Add item
                </button>
              </div>
              {Array.isArray(selectedSupply.noteItems) && selectedSupply.noteItems.length ? (
                <div className="opsui-table-wrap">
                  <table className="opsui-table">
                    <thead>
                      <tr>
                        <th>Material</th>
                        <th className="opsui-hide-sm">Store / Item #</th>
                        <th style={{ textAlign: "right" }}>Cost</th>
                        <th>Ordered</th>
                        <th>Received</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSupply.noteItems.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.material}
                            {Array.isArray(item.images) && item.images.length > 0 && (
                              <div className="opsui-thumbs" style={{ marginTop: 6 }}>
                                {item.images.map((img, i) => (
                                  <img key={i} src={img} alt="Item" style={{ width: 48, height: 48 }} />
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="opsui-hide-sm">{[item.store, item.sku && `#${item.sku}`].filter(Boolean).join(" · ") || "—"}</td>
                          <td style={{ textAlign: "right" }}>{money(item.cost)}</td>
                          <td>
                            <label className="opsui-checkrow">
                              <input type="checkbox" checked={!!item.ordered} onChange={() => toggleOrderItem(index, "ordered")} />
                            </label>
                          </td>
                          <td>
                            <label className="opsui-checkrow">
                              <input type="checkbox" checked={!!item.received} onChange={() => toggleOrderItem(index, "received")} />
                            </label>
                          </td>
                          <td className="opsui-cell-actions">
                            <button type="button" className="opsui-btn is-danger is-sm" onClick={() => deleteOrderItem(index)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="opsui-card-body opsui-muted" style={{ fontSize: 14 }}>
                  No extra items added.
                </div>
              )}
            </div>
          </div>
        </OpsModal>
      )}

      {/* ================= add line item ================= */}
      {itemForm && (
        <OpsModal
          title="Add item to this project"
          onClose={() => setItemForm(null)}
          width={520}
          stacked
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setItemForm(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveOrderItem} disabled={busy}>
                {busy ? "Saving…" : "Add item"}
              </button>
            </>
          }
        >
          <div className="opsui-form-grid">
            <Field label="Material" required wide>
              <input className="opsui-input" value={itemForm.material} onChange={(e) => setItemForm((p) => ({ ...p, material: e.target.value }))} />
            </Field>
            <Field label="Store">
              <input className="opsui-input" value={itemForm.store} onChange={(e) => setItemForm((p) => ({ ...p, store: e.target.value }))} />
            </Field>
            <Field label="Item #">
              <input className="opsui-input" value={itemForm.sku} onChange={(e) => setItemForm((p) => ({ ...p, sku: e.target.value }))} />
            </Field>
            <Field label="Cost">
              <input className="opsui-input" type="number" step="0.01" value={itemForm.cost} onChange={(e) => setItemForm((p) => ({ ...p, cost: e.target.value }))} />
            </Field>
            <Field label="Images">
              <input className="opsui-file" type="file" accept="image/*" multiple onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setItemForm((p) => ({ ...p, images: files }));
              }} />
            </Field>
          </div>
        </OpsModal>
      )}

      {/* ================= warehouse form ================= */}
      {warehouseForm && (
        <OpsModal
          title={warehouseForm.mode === "add" ? "Add warehouse item" : `Edit ${warehouseForm.data.itemName || "item"}`}
          onClose={() => setWarehouseForm(null)}
          stacked={!!selectedWarehouseSupply}
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setWarehouseForm(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={saveWarehouse} disabled={busy}>
                {busy ? "Saving…" : "Save item"}
              </button>
            </>
          }
        >
          <div className="opsui-form-grid">
            <Field label="Item Name" required>
              <input className="opsui-input" name="itemName" value={warehouseForm.data.itemName || ""} onChange={onWarehouseChange} />
            </Field>
            <Field label="Item Code">
              <input className="opsui-input" name="itemCode" value={warehouseForm.data.itemCode || ""} onChange={onWarehouseChange} />
            </Field>
            <Field label="Item Description" wide>
              <textarea className="opsui-textarea" name="itemDescription" value={warehouseForm.data.itemDescription || ""} onChange={onWarehouseChange} />
            </Field>
            <Field label="Category" required>
              <select className="opsui-select" name="category" value={warehouseForm.data.category || ""} onChange={onWarehouseChange}>
                <option value="">Select category</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                {warehouseForm.data.category && !CATEGORIES.includes(warehouseForm.data.category) && (
                  <option value={warehouseForm.data.category}>{warehouseForm.data.category}</option>
                )}
              </select>
            </Field>
            <Field label="Supplier">
              <input className="opsui-input" name="supplier" value={warehouseForm.data.supplier || ""} onChange={onWarehouseChange} />
            </Field>
            <Field label="Quantity" required>
              <input className="opsui-input" type="number" name="quantity" value={warehouseForm.data.quantity ?? ""} onChange={onWarehouseChange} />
            </Field>
            <Field label="Units" required>
              <select className="opsui-select" name="units" value={warehouseForm.data.units || ""} onChange={onWarehouseChange}>
                <option value="">Select units</option>
                {UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
                {warehouseForm.data.units && !UNITS.includes(warehouseForm.data.units) && (
                  <option value={warehouseForm.data.units}>{warehouseForm.data.units}</option>
                )}
              </select>
            </Field>
            <Field label="Restock Level" hint="Flag as low stock at or below this quantity">
              <input className="opsui-input" type="number" name="restockLevel" value={warehouseForm.data.restockLevel ?? ""} onChange={onWarehouseChange} />
            </Field>
            <Field label="Status">
              <select className="opsui-select" name="status" value={warehouseForm.data.status || ""} onChange={onWarehouseChange}>
                <option value="">Select status</option>
                {WAREHOUSE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <div className="opsui-section-title">Location</div>
            <div className="opsui-form-grid is-3" style={{ gridColumn: "1 / -1" }}>
              <Field label="Aisle">
                <input className="opsui-input" name="aisleNumber" value={warehouseForm.data.aisleNumber || ""} onChange={onWarehouseChange} />
              </Field>
              <Field label="Shelf">
                <input className="opsui-input" name="shelfNumber" value={warehouseForm.data.shelfNumber || ""} onChange={onWarehouseChange} />
              </Field>
              <Field label="Bin">
                <input className="opsui-input" name="binNumber" value={warehouseForm.data.binNumber || ""} onChange={onWarehouseChange} />
              </Field>
            </div>
            <Field label={warehouseForm.mode === "add" ? "Images" : "Add images"} wide hint={warehouseForm.files.length ? `${warehouseForm.files.length} image(s) will upload on save.` : undefined}>
              <input
                className="opsui-file"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setWarehouseForm((p) => ({ ...p, files }));
                }}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {/* ================= warehouse details ================= */}
      {selectedWarehouseSupply && (
        <OpsModal
          title={selectedWarehouseSupply.itemName || "Warehouse item"}
          subtitle={selectedWarehouseSupply.category}
          onClose={() => setSelectedWarehouseSupply(null)}
          footer={
            <>
              <div className="opsui-foot-left">
                <button
                  type="button"
                  className="opsui-btn is-danger"
                  onClick={() => setDeleteTarget({ type: "warehouse", item: selectedWarehouseSupply })}
                >
                  Delete
                </button>
              </div>
              <button type="button" className="opsui-btn" onClick={() => setNoteData({ text: "", images: [] })}>
                Add note
              </button>
              <button type="button" className="opsui-btn" onClick={openEditWarehouse}>
                Edit
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={() => setUseItemData({ quantity: "", user: "", job: "" })}>
                Use item
              </button>
            </>
          }
        >
          {(() => {
            const s = selectedWarehouseSupply;
            const st = stockState(s);
            const notes = Array.isArray(s.notes) ? [...s.notes].reverse() : [];
            return (
              <div style={{ display: "grid", gap: 18 }}>
                <div className="opsui-stats" style={{ marginBottom: 0 }}>
                  <div className={`opsui-stat ${st ? (st.tone === "danger" ? "is-danger" : "is-warn") : "is-success"}`}>
                    <div className="opsui-stat-label">On hand</div>
                    <div className="opsui-stat-value">
                      {s.quantity ?? "—"} <span style={{ fontSize: 14, fontWeight: 500 }}>{s.units}</span>
                    </div>
                  </div>
                  <div className="opsui-stat">
                    <div className="opsui-stat-label">Restock level</div>
                    <div className="opsui-stat-value">{s.restockLevel || "—"}</div>
                  </div>
                </div>
                <div className="opsui-pills">
                  {st && <Pill tone={st.tone}>{st.label}</Pill>}
                  {s.status && <StatusPill status={s.status} />}
                </div>
                <dl className="opsui-dl">
                  <div><dt>Item Code</dt><dd>{s.itemCode || "—"}</dd></div>
                  <div><dt>Supplier</dt><dd>{s.supplier || "—"}</dd></div>
                  <div className="is-wide"><dt>Description</dt><dd>{s.itemDescription || "—"}</dd></div>
                  <div className="is-wide"><dt>Location</dt><dd>{location(s) || "—"}</dd></div>
                  <div><dt>Checked In</dt><dd>{s.checkedDate || "—"}</dd></div>
                  <div><dt>Checked By</dt><dd>{s.checkedBy || "—"}</dd></div>
                </dl>
                {Array.isArray(s.images) && s.images.length > 0 && (
                  <div className="opsui-thumbs">
                    {s.images.map((img, i) => (
                      <a key={i} href={img} target="_blank" rel="noopener noreferrer">
                        <img src={img} alt={`Warehouse Supply ${i + 1}`} />
                      </a>
                    ))}
                  </div>
                )}
                <div>
                  <div className="opsui-label" style={{ marginBottom: 8 }}>Activity</div>
                  {notes.length ? (
                    <ul className="opsui-list">
                      {notes.map((note, index) => (
                        <li key={index} className="opsui-list-item">
                          <div className="opsui-list-item-body">
                            <div className="opsui-list-meta">{note.timestamp}</div>
                            {note.quantity !== undefined && note.text === undefined ? (
                              <div style={{ fontSize: 14 }}>
                                <Pill tone="info" plain>Used {note.quantity}</Pill>{" "}
                                by <strong>{note.user}</strong>
                                {(note.jobLabel || note.job) && (
                                  <span className="opsui-muted">
                                    {" "}
                                    · {note.jobLabel || (() => {
                                      const j = jobs.find((x) => x.id === note.job);
                                      return j ? [j.siteName, j.scope].filter(Boolean).join(" - ") : note.job;
                                    })()}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div style={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{note.text}</div>
                            )}
                            {Array.isArray(note.images) && note.images.length > 0 && (
                              <div className="opsui-thumbs" style={{ marginTop: 6 }}>
                                {note.images.map((img, idx) => (
                                  <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                                    <img src={img} alt={`Note ${index + 1} ${idx + 1}`} style={{ width: 64, height: 64 }} />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="opsui-muted" style={{ fontSize: 14 }}>No notes available.</span>
                  )}
                </div>
              </div>
            );
          })()}
        </OpsModal>
      )}

      {/* ================= use item ================= */}
      {useItemData && selectedWarehouseSupply && (
        <OpsModal
          title="Use item"
          subtitle={`${selectedWarehouseSupply.itemName} — ${selectedWarehouseSupply.quantity ?? 0} ${selectedWarehouseSupply.units || ""} on hand`}
          onClose={() => setUseItemData(null)}
          width={480}
          stacked
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setUseItemData(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={handleUseItemSubmit} disabled={busy}>
                {busy ? "Saving…" : "Record usage"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 12 }}>
            <Field label="Quantity" required>
              <input
                className="opsui-input"
                type="number"
                min="1"
                value={useItemData.quantity}
                onChange={(e) => setUseItemData((p) => ({ ...p, quantity: e.target.value }))}
              />
            </Field>
            <Field label="For which user" required>
              <select className="opsui-select" value={useItemData.user} onChange={(e) => setUseItemData((p) => ({ ...p, user: e.target.value }))}>
                <option value="">Select user</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {userName(u)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="For which job">
              <select className="opsui-select" value={useItemData.job} onChange={(e) => setUseItemData((p) => ({ ...p, job: e.target.value }))}>
                <option value="">Select job</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {[job.siteName, job.scope].filter(Boolean).join(" - ")}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </OpsModal>
      )}

      {/* ================= add note (warehouse) ================= */}
      {noteData && selectedWarehouseSupply && (
        <OpsModal
          title="Add note"
          subtitle={selectedWarehouseSupply.itemName}
          onClose={() => setNoteData(null)}
          width={500}
          stacked
          footer={
            <>
              <button type="button" className="opsui-btn" onClick={() => setNoteData(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="opsui-btn is-primary" onClick={handleAddNoteSubmit} disabled={busy}>
                {busy ? "Saving…" : "Save note"}
              </button>
            </>
          }
        >
          <div style={{ display: "grid", gap: 12 }}>
            <Field label="Note" required>
              <textarea className="opsui-textarea" value={noteData.text} onChange={(e) => setNoteData((p) => ({ ...p, text: e.target.value }))} />
            </Field>
            <Field label="Images">
              <input
                className="opsui-file"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setNoteData((p) => ({ ...p, images: files }));
                }}
              />
            </Field>
          </div>
        </OpsModal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete?"
          message={
            <>
              <strong>{deleteTarget.type === "supply" ? deleteTarget.item.material : deleteTarget.item.itemName}</strong> will be
              permanently removed.
            </>
          }
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          busy={busy}
        />
      )}
    </div>
  );
};

export default Supplies;
